// src/modules/app-views/collaborators/pages/Init/modals/CollaboratorsParametersModal/tabs/DepartmentsTab.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useState, useCallback, useMemo } from 'react';
import { Table, Button, Flex, Space, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import appService from 'services/appService';
import { generateUidV4 } from 'utils/helpers';
import DepartmentFormModal from './DepartmentFormModal';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const DepartmentsTab = ({ loading, setLoading }) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 2. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [showModal, setShowModal] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [mousePosition, setMousePosition] = useState(null);

  // Use local state for departments - initialize from store, update locally after save
  const [departments, setDepartments] = useState(() => {
    const rawDepartments = appService.getModulePreferences({ category: 'departments' });
    return normalizeDepartments(rawDepartments);
  });

  // Filter out removed departments for display in table
  const visibleDepartments = useMemo(() => departments.filter(d => !d.removed), [departments]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleAdd = useCallback(e => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(null);
    setShowModal(true);
  }, []);

  const handleEdit = useCallback((record, e) => {
    setMousePosition({ x: e.clientX, y: e.clientY });
    setEditingRecord(record);
    setShowModal(true);
  }, []);

  const handleSave = useCallback(
    async formData => {
      // Check for duplicates (excluding current item if editing, and excluding removed)
      const isDuplicate = departments.some(
        d =>
          d.uid !== editingRecord?.uid &&
          !d.removed &&
          d.label.toLowerCase() === formData.label.toLowerCase()
      );

      if (isDuplicate) {
        appService.message({ flag: 'w', text: 'item_already_exists' });
        return;
      }

      setLoading(true);
      try {
        let updatedDepartments;

        if (editingRecord) {
          // Editing existing department - keep the same uid and preserve removed flag
          updatedDepartments = departments.map(d =>
            d.uid === editingRecord.uid
              ? { ...d, label: formData.label }
              : d
          );
        } else {
          // Adding new department - generate new uid
          updatedDepartments = [
            ...departments,
            {
              uid: generateUidV4(),
              label: formData.label,
            },
          ];
        }

        // Save array directly: departments: [...]
        await appService.updateModulePreferences({
          category: 'departments',
          data: updatedDepartments,
        });

        // Update local state immediately (don't wait for SSE)
        setDepartments(updatedDepartments);

        appService.message({ flag: 's', text: 'saved_successfully' });
        setShowModal(false);
        setEditingRecord(null);
      } catch (error) {
        console.error('[DepartmentsTab] Save error:', error);
        appService.message({ flag: 'e', text: 'error_saving' });
      } finally {
        setLoading(false);
      }
    },
    [departments, editingRecord, setLoading]
  );

  const handleCancel = useCallback(() => {
    setShowModal(false);
    setEditingRecord(null);
  }, []);

  const handleDelete = useCallback(
    async record => {
      setLoading(true);
      try {
        // Hard delete: remove from array
        const updatedDepartments = departments.filter(d => d.uid !== record.uid);
        appService.message({ flag: 's', text: 'removed_successfully' });

        // Save array directly: departments: [...]
        await appService.updateModulePreferences({
          category: 'departments',
          data: updatedDepartments,
        });

        // Update local state immediately (don't wait for SSE)
        setDepartments(updatedDepartments);
      } catch (error) {
        console.error('[DepartmentsTab] Delete error:', error);
        appService.message({ flag: 'e', text: 'error_removing' });
      } finally {
        setLoading(false);
      }
    },
    [departments, setLoading]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. TABLE COLUMNS
  // ─────────────────────────────────────────────────────────────────────────────
  const columns = [
    {
      title: translateX('department'),
      dataIndex: 'label',
      render: text => <span className="font-weight-bold">{text}</span>,
    },
    {
      title: translateX('actions'),
      dataIndex: 'actions',
      width: 100,
      align: 'center',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="text"
            size="small"
            icon={<EditOutlined />}
            onClick={e => handleEdit(record, e)}
            disabled={loading}
          />
          <Popconfirm
            title={translateX('confirm_delete')}
            onConfirm={() => handleDelete(record)}
            okText={translateX('yes')}
            cancelText={translateX('no')}
          >
            <Button type="text" size="small" danger icon={<DeleteOutlined />} disabled={loading} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <Flex vertical style={{ height: '100%' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Table
          rowKey="uid"
          columns={columns}
          dataSource={visibleDepartments}
          pagination={false}
          size="small"
          loading={loading}
          locale={{ emptyText: translateX('no_data') }}
          scroll={{ y: 'calc(80vh - 240px)' }}
        />
      </div>

      <Flex justify="flex-end" className="mt-3" style={{ flexShrink: 0 }}>
        <Button icon={<PlusOutlined />} onClick={handleAdd} disabled={loading}>
          {translateX('add_department')}
        </Button>
      </Flex>

      <DepartmentFormModal
        open={showModal}
        onCancel={handleCancel}
        onSave={handleSave}
        initialData={editingRecord}
        loading={loading}
        mousePosition={mousePosition}
      />
    </Flex>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default DepartmentsTab;

// *******************************************************************************
// HELPERS
// *******************************************************************************
function normalizeDepartments(rawData) {
  if (!rawData || !Array.isArray(rawData)) return [];
  return rawData.map(normalizeDepartment);
}

function normalizeDepartment(department) {
  return {
    uid: department.uid,
    label: department.label || '',
    ...(department.removed && { removed: true }),
  };
}
